import React from "react";
import { ClientMtUsageReport, ClientMtUsageReportItem, MetricsService, MtBillingType } from "gen/clients";
import { ExportButton } from "components/ExportButton/ExportButton";
import { default as dayjs } from "dayjs";
import { describeMtBillingType } from "../../../SystemSettingsPage/tabs/ApplcationsPage/utils/MtUtils";
import { secondsToMinutes } from "utils/dateUtils";

interface Props {
  from: Date;
  to: Date;
  billingType?: MtBillingType;
}

interface ExportedDataRow {
  clientName: string;
  clientId: string;
  branchName: string;
  userCount: number;
  billingType: string;
  documentCharactersCharged: number;
  transcription: number;
  transcriptionAndTranslation: number;
  translationAndDubbing: number;
  translationAndSubtitling: number;
  [key: string]: string | number;
}

const columnHeaders = {
  clientName: "Client",
  clientId: "Client ID",
  branchName: "Branch",
  billingType: "Billing Type",
  userCount: "User Count",
  documentCharactersCharged: "Document Translation",
  transcription: "Transcription",
  transcriptionAndTranslation: "Transcription And Translation",
  translationAndDubbing: "Translation And Dubbing",
  translationAndSubtitling: "Translation And Subtitling"
};

const ExportAnalyticsButton: React.FC<Props> = ({ from, to, billingType }) => {
  const getExportData = React.useCallback(async (): Promise<ExportedDataRow[]> => {
    let allItems: ClientMtUsageReportItem[] = [];
    let nextPageToken: string | undefined = undefined;

    // Keep fetching until no more pages
    do {
      const response: ClientMtUsageReport = await MetricsService.getClientMtUsageReport({
        fromDate: from.toISOString(),
        toDate: to.toISOString(),
        pageToken: nextPageToken,
        pageSize: 100, // Fetch 100 items at a time
        billingType
      });

      allItems = [...allItems, ...response.items];
      nextPageToken = response.nextPageToken;
    } while (nextPageToken);

    // Map the data to match the CSV columns
    return allItems.map(row => ({
      clientName: row.clientName,
      clientId: row.clientId,
      branchName: row.branchName || "",
      userCount: row.usernames.length,
      billingType: describeMtBillingType(row.billingType),
      documentCharactersCharged: row.charactersChargedByUserRequest?.FILES_TRANSLATION || 0,
      transcription: secondsToMinutes(row.secondsChargedByUserRequest?.MEDIA_TRANSCRIBE || 0),
      transcriptionAndTranslation: secondsToMinutes(row.secondsChargedByUserRequest?.MEDIA_TRANSLATE || 0),
      translationAndDubbing: secondsToMinutes(row.secondsChargedByUserRequest?.MEDIA_DUBBING || 0),
      translationAndSubtitling: secondsToMinutes(row.secondsChargedByUserRequest?.MEDIA_SUBTITLE_EMBEDDING || 0)
    }));
  }, [billingType, from, to]);

  const fileName = `mt-usage-for-client-billing-${dayjs(from).format("MM-DD-YYYY")}-${dayjs(to).format("MM-DD-YYYY")}.csv`;

  return (
    <ExportButton<ExportedDataRow>
      filename={fileName}
      columnHeaders={columnHeaders}
      getExportData={getExportData}
      exportingMessage="Exporting records. This may take a moment..."
    />
  );
};

export { ExportAnalyticsButton };
